import React, {useEffect, useState} from 'react';
import styles from './FilterRequests.module.css';
import {
    Checkbox,
    DefaultButton,
    Dropdown,
    PrimaryButton,
    Stack,
    StackItem,
    TextField
} from "@fluentui/react";
import {fetchRequestFilterOptions} from "../../../services/RequestServices/ActiveRequestService";
import {useAuth} from "react-oidc-context";
import {IDropdownOption} from "@fluentui/react/lib/Dropdown";
import {
    Authority,
    EuSecLegBasis,
    Status,
    RequestFilterOptionsDto,
    PersonType
} from "../../../model/RequestFilterOptions";
import {Tab} from "../ViewRequests/ViewRequestsUtils";
import strings from "../../../loc/strings";
import {IRequestFilters} from "../../../model/RequestFilters";
import {getUserInfo} from "../../../services/UserInfoService";
import {ROLE_CA_END_USER} from "../../../constants/EiopaFnPConstants";
import {DatePickerPropsOut, stringToDate} from "../../Utils";
import {DatePickerProps, DateProps, FnpDatePicker} from "../../Utils";

interface FilterRequestsProps {
    selectedTab: string;
    filters: IRequestFilters;
    setFilters: any;
    onClickReset: any;
    onClickFilter: any;
    resetFlag: boolean;
}

interface FilterDropdownOptions {
    authorities: IDropdownOption[];
    euSecLegBases: IDropdownOption[];
    statuses: IDropdownOption[];
    personTypes: IDropdownOption[];
}
export interface DatePickersState {
    SubmissionDateFrom: DateProps;
    SubmissionDateTo: DateProps;
    CreationDateFrom: DateProps;
    CreationDateTo: DateProps;
}
function FilterRequests(props: FilterRequestsProps) {
    const auth= useAuth();

    let userInfo = getUserInfo(auth.user?.id_token);

    const isCAEndUser: boolean = userInfo.roles.includes(ROLE_CA_END_USER);

    const filterDropdownOptionsInit:FilterDropdownOptions = {
        authorities: [],
        euSecLegBases: [],
        statuses: [],
        personTypes: []
    }
    const [filterDropdownOptions, setFilterDropdownOptions] = useState<FilterDropdownOptions>(filterDropdownOptionsInit);

    const [datePickersState, setDatePickersState] = useState<DatePickersState>({
        SubmissionDateTo: {
            value: props.filters.submissionDateTo,
            isValid: true,
            isFutureDate: false,
        },
        SubmissionDateFrom: {value: props.filters.submissionDateFrom, isValid: true, isFutureDate: false},
        CreationDateFrom: {value: props.filters.creationDateFrom, isValid: true, isFutureDate: false},
        CreationDateTo: {value: props.filters.creationDateFrom, isValid: true, isFutureDate: false}
    });

    useEffect(() => {
        (function () {
            fetchRequestFilterOptions(auth.user?.id_token).then(
                (response: RequestFilterOptionsDto | undefined) => {
                    if(response === undefined) return;

                    const authoritiesDD: IDropdownOption[] = [];
                    response.authorities.forEach((authority: Authority) => {authoritiesDD.push({key: authority.code, text: authority.name, title: authority.name});});

                    const euSecLegBasesDD: IDropdownOption[] = [];
                    response.euSecLegBases.forEach((euSecLeg: EuSecLegBasis) => {euSecLegBasesDD.push({key: euSecLeg.id, text: euSecLeg.name, title: euSecLeg.name});});

                    const statusesDD: IDropdownOption[] = []
                    response.statuses.forEach((status: Status) => {statusesDD.push({key: status.id, text: status.name, title: status.name});});

                    const personTypesDD: IDropdownOption[] = []
                    response.types.forEach((type: PersonType) => {personTypesDD.push({key: type.id, text: type.title, title: type.title});});

                    setFilterDropdownOptions({
                        authorities: authoritiesDD,
                        euSecLegBases: euSecLegBasesDD,
                        statuses: statusesDD,
                        personTypes: personTypesDD
                    });
                }
            ).catch();
        })();
        return () => { };
    }, []);

    // on setFilters reset datePickersState
    useEffect(() => {
        resetDatePickersState();
    }, [props.selectedTab]);
    function resetDatePickersState() {
        setDatePickersState({
            SubmissionDateTo: {
                value: undefined,
                isValid: true, isFutureDate: false
            },
            SubmissionDateFrom: {value: undefined, isValid: true, isFutureDate: false},
            CreationDateFrom: {value: undefined, isValid: true, isFutureDate: false},
            CreationDateTo: {value: undefined, isValid: true, isFutureDate: false}
        });
    }
    function onChangeRequestNo(event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) {
        props.setFilters((prevState: IRequestFilters) => {return {...prevState, requestNo: newValue ?? ""}});
    }

    function onChangeRequestedAuthority(event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption) {
        props.setFilters((prevState: IRequestFilters) => {return {...prevState, requestedAuthority: option === undefined? "" : option.key as string}});
    }

    function onChangeRequestingAuthority(event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption) {
        props.setFilters((prevState: IRequestFilters) => {return {...prevState, requestingAuthority: option === undefined? "" : option.key as string}});
    }

    function onChangeEuSecLegBasis(event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption) {
        props.setFilters((prevState: IRequestFilters) => {return {...prevState, euSecLegBasis: option === undefined? "" : option.key as string}});
    }

    function onChangeStatus(event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption) {
        props.setFilters((prevState: IRequestFilters) => {return {...prevState, status: option === undefined? "" : option.key as string}});
    }

    function onChangeType(event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption) {
        props.setFilters((prevState: IRequestFilters) => {return {...prevState, type: option === undefined? "" : option.key as string}});
    }

    function onSelectSubmissionDateFrom(date: Date | null | undefined) {
        props.setFilters((prevState: IRequestFilters) => {return {...prevState, submissionDateFrom: date === null ? undefined : date}});
    }

    function onSelectSubmissionDateTo(date: Date | null | undefined) {
        props.setFilters((prevState: IRequestFilters) => {return {...prevState, submissionDateTo: date === null ? undefined : date}});
    }

    function onSelectCreationDateFrom(date: Date | null | undefined) {
        props.setFilters((prevState: IRequestFilters) => {return {...prevState, creationDateFrom: date === null ? undefined : date}});
    }

    function onSelectCreationDateTo(date: Date | null | undefined) {
        props.setFilters((prevState: IRequestFilters) => {return {...prevState, creationDateTo: date === null ? undefined : date}});
    }

    function onChangeInitiatorCheckbox(ev?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) {
        props.setFilters((prevState: IRequestFilters) => {return {...prevState, isInitiator: !!checked}});
    }

    function onFormatDate(date?: Date): string {
        if (date) {
            let day = date.getDate();
            let month = date.getMonth() + 1;
            let dayString = day < 10 ? '0' + day.toString() : day.toString();
            let monthString = month < 10 ? '0' + month.toString() : month.toString();

            return dayString + '/' + monthString + '/' + date.getFullYear();
        }

        return '';
    }


    function filtersPerTab():JSX.Element {
        switch (props.selectedTab) {
            case Tab.Active:
                return <></>;
            case Tab.Outgoing:
            case Tab.Incoming:
                return (
                    <StackItem className={styles.FiltersSection}>
                        <Stack className={styles.FiltersChunk}>
                            <StackItem>
                                <TextField
                                    label={strings.filterLabelRequestNo}
                                    placeholder={strings.filterPlaceholderRequestNo}
                                    styles={{root: {width: 220, marginRight: '30px'}}}
                                    value={props.filters.requestNo}
                                    onChange={onChangeRequestNo}
                                />
                            </StackItem>
                            <StackItem>
                                {props.selectedTab === Tab.Outgoing ?
                                    <Dropdown
                                        label={strings.filterLabelRequestedAuthority}
                                        ariaLabel={strings.filterLabelRequestedAuthority}
                                        placeholder={strings.filterPlaceholderRequestAuthority}
                                        options={filterDropdownOptions.authorities}
                                        selectedKey={props.filters.requestedAuthority}
                                        onChange={onChangeRequestedAuthority}
                                        styles={{root: {width: 220, marginRight: '30px'}}}
                                    />
                                :
                                    <Dropdown
                                        label={strings.filterLabelRequestingAuthority}
                                        ariaLabel={strings.filterLabelRequestingAuthority}
                                        placeholder={strings.filterPlaceholderRequestAuthority}
                                        options={filterDropdownOptions.authorities}
                                        selectedKey={props.filters.requestingAuthority}
                                        onChange={onChangeRequestingAuthority}
                                        styles={{root: {width: 220, marginRight: '30px'}}}
                                    />
                                }
                            </StackItem>
                        </Stack>
                        <Stack className={styles.FiltersChunk}>
                            <StackItem>
                                <Dropdown
                                    label={strings.filterLabelEuSecLegBasis}
                                    ariaLabel={strings.filterLabelEuSecLegBasis}
                                    placeholder={strings.filterPlaceholderEuSecLegBasis}
                                    options={filterDropdownOptions.euSecLegBases}
                                    selectedKey={props.filters.euSecLegBasis}
                                    onChange={onChangeEuSecLegBasis}
                                    styles={{root: {width: 220, marginRight: '30px'}}}
                                />
                            </StackItem>
                            <StackItem>
                                <Dropdown
                                    label={strings.filterLabelStatus}
                                    ariaLabel={strings.filterLabelStatus}
                                    placeholder={strings.filterPlaceholderStatus}
                                    options={filterDropdownOptions.statuses}
                                    selectedKey={props.filters.status}
                                    onChange={onChangeStatus}
                                    styles={{root: {width: 220, marginRight: '30px'}}}
                                />
                            </StackItem>
                        </Stack>
                        <Stack className={styles.FiltersChunk}>
                            <StackItem className={styles.SubmissionDateFromToCouple}>
                                <FnpDatePicker
                                    datePickerKey={"submissionDateFrom"}
                                    isRequired={false}
                                    date={props.filters.submissionDateFrom ?? new Date()}
                                    resetFlag={props.resetFlag}
                                    resetInitDate={true}
                                    setSelectedDate={(datePickerProps: DatePickerPropsOut) => {
                                        datePickersState.SubmissionDateFrom.dateString = datePickerProps.value;
                                        const date = stringToDate(datePickerProps.value);
                                        datePickersState.SubmissionDateFrom.value = date;
                                        datePickersState.SubmissionDateFrom.isValid = datePickerProps.valid ?? true;
                                        onSelectSubmissionDateFrom(date);
                                    }}
                                    label={strings.filterLabelSubmissionDate}
                                    initialPickerDate={new Date()}
                                    ariaLabel={strings.filterLabelSubmissionDate}
                                    placeholder={strings.filterPlaceholderDateFrom}
                                    isOutOfBoundsErrorMessage={"Future dates are not allowed"}
                                    styles={{
                                        root: {
                                            width: 160,
                                            marginRight: '10px'
                                        },
                                        statusMessage: {
                                            display: 'none'
                                        }
                                    }}
                                    ></FnpDatePicker>
                                <FnpDatePicker
                                    datePickerKey={"submissionDateTo"}
                                    isRequired={false}
                                    date={props.filters.submissionDateTo ?? new Date()}
                                    resetFlag={props.resetFlag}
                                    resetInitDate={true}
                                    setSelectedDate={(datePickerProps: DatePickerPropsOut) => {
                                        datePickersState.SubmissionDateTo.dateString = datePickerProps.value;
                                        const date = stringToDate(datePickerProps.value);
                                        datePickersState.SubmissionDateTo.value = date;
                                        datePickersState.SubmissionDateTo.isValid = datePickerProps.valid ?? true;
                                        onSelectSubmissionDateTo(date);
                                    }}
                                    initialPickerDate={new Date()}
                                    ariaLabel={strings.filterLabelSubmissionDate}
                                    placeholder={strings.filterPlaceholderDateTo}
                                    isOutOfBoundsErrorMessage={"Future dates are not allowed"}
                                    styles={{
                                        root: {width: 160, marginRight: '30px', marginTop: '29px'},
                                        statusMessage: {
                                            display: 'none'
                                        }
                                    }}
                                ></FnpDatePicker>
                            </StackItem>
                            {isCAEndUser && props.selectedTab === Tab.Outgoing &&
                                <StackItem>
                                    <Checkbox
                                        label={strings.filterLabelInitiatorCheckbox}
                                        ariaLabel={strings.filterLabelInitiatorCheckbox}
                                        checked={props.filters.isInitiator}
                                        onChange={onChangeInitiatorCheckbox}
                                        styles={{root: {marginTop: '35px'}}}
                                    />
                                </StackItem>
                            }
                        </Stack>
                    </StackItem>
                );
            case Tab.Draft:
                return (
                    <StackItem className={styles.FiltersSection}>
                        <Stack className={styles.FiltersChunk}>
                            <StackItem>
                                <Dropdown
                                    label={strings.filterLabelRequestedAuthority}
                                    ariaLabel={strings.filterLabelRequestedAuthority}
                                    placeholder={strings.filterPlaceholderRequestAuthority}
                                    options={filterDropdownOptions.authorities}
                                    selectedKey={props.filters.requestedAuthority}
                                    onChange={onChangeRequestedAuthority}
                                    styles={{root: {width: 220, marginRight: '30px'}}}
                                />
                            </StackItem>
                            <StackItem>
                                <Dropdown
                                    label={strings.filterLabelType}
                                    ariaLabel={strings.filterLabelType}
                                    placeholder={strings.filterPlaceholderType}
                                    options={filterDropdownOptions.personTypes}
                                    selectedKey={props.filters.type}
                                    onChange={onChangeType}
                                    styles={{root: {width: 220, marginRight: '30px'}}}
                                />
                            </StackItem>
                            <StackItem className={styles.SubmissionDateFromToCouple}>
                                <FnpDatePicker
                                    datePickerKey={"creationDateFrom"}
                                    isRequired={false}
                                    date={props.filters.creationDateFrom ?? new Date()}
                                    resetFlag={props.resetFlag}
                                    resetInitDate={true}
                                    setSelectedDate={(datePickerProps: DatePickerPropsOut) => {
                                        datePickersState.CreationDateFrom.dateString = datePickerProps.value;
                                        const date = stringToDate(datePickerProps.value);
                                        datePickersState.CreationDateFrom.value = date;
                                        datePickersState.CreationDateFrom.isValid = datePickerProps.valid ?? true;
                                        onSelectCreationDateFrom(date);
                                    }}
                                    initialPickerDate={new Date()}
                                    label={strings.filterLabelCreationDate}
                                    ariaLabel={strings.filterLabelCreationDate}
                                    placeholder={strings.filterPlaceholderDateFrom}
                                    isOutOfBoundsErrorMessage={"Future dates are not allowed"}
                                    styles={{root: {width: 160, marginRight: '10px'},
                                        statusMessage: {
                                            display: 'none'
                                        }
                                    }}
                                ></FnpDatePicker>
                                <FnpDatePicker
                                    datePickerKey={"creationDateFrom"}
                                    isRequired={false}
                                    date={props.filters.creationDateTo ?? new Date()}
                                    resetFlag={props.resetFlag}
                                    resetInitDate={true}
                                    setSelectedDate={(datePickerProps: DatePickerPropsOut) => {
                                        datePickersState.CreationDateTo.dateString = datePickerProps.value;
                                        const date = stringToDate(datePickerProps.value);
                                        datePickersState.CreationDateTo.value = date;
                                        datePickersState.CreationDateTo.isValid = datePickerProps.valid ?? true;
                                        onSelectCreationDateTo(date);
                                    }}
                                    initialPickerDate={new Date()}
                                    ariaLabel={strings.filterPlaceholderDateTo}
                                    placeholder={strings.filterPlaceholderDateTo}
                                    isOutOfBoundsErrorMessage={"Future dates are not allowed"}
                                    styles={{root: {width: 160, marginRight: '30px', marginTop: '29px'},
                                        statusMessage: {
                                            display: 'none'
                                        }
                                    }}
                                ></FnpDatePicker>
                            </StackItem>
                        </Stack>
                    </StackItem>
                );
            default:
                return <></>
        }
    }

    const filtersChunk = filtersPerTab();

    return (
        props.selectedTab === Tab.Active ?
            <></>
            :
            <Stack className={styles.FiltersWrapper}>
                {filtersChunk}
                <StackItem className={styles.FiltersActionButtons}>
                    <PrimaryButton
                        text={strings.filterButtonFilter}
                        onClick={() => {
                            if (datePickersState.SubmissionDateFrom.isValid && datePickersState.SubmissionDateTo.isValid && datePickersState.CreationDateFrom.isValid && datePickersState.CreationDateTo.isValid) {
                                props.onClickFilter();
                            }
                        }}
                        styles={{root: {marginRight: "15px"}}}
                    />
                    <DefaultButton
                        text={strings.filterButtonReset}
                        onClick={() => {
                            // resetDatePickersState();
                            props.onClickReset();
                        }}
                    />
                </StackItem>
            </Stack>
    )
}

export default FilterRequests;
